import {createAsyncThunk, createSlice, createSelector} from "@reduxjs/toolkit"
import axios from "axios"
import { workspaceSlice } from "./workspaceSlice"
import { deleteNote } from "./workspaceNoteSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

export const getUnassignedArticles = createAsyncThunk(
	"unassignedArticlesEventsSlice/getUnassignedArticles",
	async (workspaceId, {rejectWithValue}) => {
		try {
			const {data: articles} = await axios
				.get(`/api/workspace/${workspaceId}/article/unassigned`)

			return {articles, workspaceId}
		} catch (error) {
			console.error("failed to get Unassigned Articles: ", error)
			return rejectWithValue("Failed to get Unassigned Articles")
		}
	}
)

export const getAllUnassignedArticles = createAsyncThunk(
	"unassignedArticlesEventsSlice/getAllUnassignedArticles",
	async (workspaceId, {rejectWithValue}) => {
		try {
			const {data: articles} = await axios.get(`/api/workspace/${workspaceId}/article/unassigned/all`)

			return {articles, workspaceId}
		} catch (error) {
			console.error("failed to get All Unassigned Articles: ", error)
			return rejectWithValue("Failed to get all Unassigned Articles")
		}
	}
)

//TODO: this isn't paginating, unassigned is stuck at 20 count...
export const incrementPageAndFetchArticles = createAsyncThunk(
	"unassignedArticlesEventsSlice/incrementPageAndFetchArticles",
	async ({ workspaceId, page }, {getState, rejectWithValue}) => {
		const state = getState()
		try {
			const fetchedCount = state.unassignedArticlesEvent.byWorkspace[workspaceId].fetchedCount

			const {data: articles} = await axios
				.get(`/api/workspace/${workspaceId}/article/unassigned`, {params: {page, fetchedCount}})
			return {articles, workspaceId}
		} catch (error) {
			console.error("failed to increment page and fetch next Unassigned Articles: ", error)
			return rejectWithValue("Failed to get next page of Unassigned Articles")
		}
	}
)

const initialState = {
	allUnassignedArticles: [],
	unassignedArticles: [],
	hasMore: true,
	currentPage: 1,
	fetched: 0,
	error: null,
}
const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)
export const unassignedArticlesEventsSlice = createSlice({
	name: "unassignedArticlesEventsSlice",
	initialState: {
		value: {
			allUnassignedArticles: [],
			unassignedArticles: [],
			hasMore: true,
			currentPage: 1,
			fetched: 0,
		},
		byWorkspace: {}
	},
	reducers: {
		clearUnassigned: (state, action) => {
			state.byWorkspace[action.payload] = {...initialState}
		},
		concatUnassignedArticles: (state, action) => {
			const {articles, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.unassignedArticles = workspace.unassignedArticles.concat(articles)
			workspace.allUnassignedArticles = workspace.allUnassignedArticles.concat(articles)
		},
		removeUnassignedArticles: (state, action) => {
			const {articles, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			// remove from unassigned paged list
			const articleIdsToRemove = articles.map(article => article.articleId)
			workspace.unassignedArticles = workspace.unassignedArticles.filter(article => !articleIdsToRemove.includes(article.articleId))
			// remove from unassigned master list
			workspace.allUnassignedArticles = workspace.allUnassignedArticles.filter(article => !articleIdsToRemove.includes(article.articleId))
			workspace.fetchedCount -= articleIdsToRemove.length
		},
	},
	extraReducers: (builder) => {
		builder.addCase(deleteNote.fulfilled, (state, action) => {
			const { unassignedArticles, workspaceId } = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.unassignedArticles = workspace.unassignedArticles.concat(unassignedArticles)
			workspace.fetchedCount += unassignedArticles.length
		})
		builder.addCase(getUnassignedArticles.fulfilled, (state, action) => {
			const {articles, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.unassignedArticles = articles
			workspace.currentPage = 1
			workspace.hasMore = articles.length > 0
			workspace.fetchedCount = articles.length
		})
		builder.addCase(incrementPageAndFetchArticles.fulfilled, (state, action) => {
			const {articles, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.unassignedArticles = workspace.unassignedArticles.concat(articles)
			workspace.currentPage += 1
			workspace.hasMore = articles.length > 0
			workspace.fetchedCount += articles.length
		})
		builder.addCase(
			workspaceSlice.actions.setPrimaryWorkspace,
			(state, action) => {
				unassignedArticlesEventsSlice.caseReducers.clearUnassigned(state, {payload: action.payload.workspace.id})
			}
		)
		builder.addCase(getAllUnassignedArticles.fulfilled, (state, action) => {
			const {articles, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.allUnassignedArticles = articles
		})
	}
})

export const { concatUnassignedArticles, removeUnassignedArticles } = unassignedArticlesEventsSlice.actions

export const selectAllUnassignedArticles = (state, workspaceId) => state.unassignedArticlesEvent.byWorkspace[workspaceId]?.allUnassignedArticles ?? initialState.allUnassignedArticles
export const selectUnassignedArticles = (state, workspaceId) => state.unassignedArticlesEvent.byWorkspace[workspaceId]?.unassignedArticles ?? initialState.unassignedArticles

// Memoized selectors for article IDs
export const selectAllUnassignedArticleIds = (workspaceId) => createSelector(
	[(state) => selectAllUnassignedArticles(state, workspaceId)],
	(allUnassignedArticles) => allUnassignedArticles.map(article => article.articleId)
)

export const selectUnassignedArticleIds = (workspaceId) => createSelector(
	[(state) => selectUnassignedArticles(state, workspaceId)],
	(unassignedArticles) => unassignedArticles.map(article => article.articleId)
)

// Non-memoized selectors for pagination and other values
export const selectHasMoreUnassignedArticles = (state, workspaceId) => state.unassignedArticlesEvent.byWorkspace[workspaceId]?.hasMore ?? initialState.hasMore
export const selectCurrentPage = (state, workspaceId) => state.unassignedArticlesEvent.byWorkspace[workspaceId]?.currentPage ?? initialState.currentPage.currentPage

export default unassignedArticlesEventsSlice.reducer
